import { Sale, SaleFormData, SaleWithProduct, SaleWithDetails, SaleFromOrderData, SaleStats } from '../types/sale';
import { Product } from '../types/product';
import { OrderWithDetails } from '../types/order';
import { supabase } from '../lib/supabase';
import { uploadImage } from './cloudinaryService';

// Exportar para que sea considerado un módulo
export {};

// Convertir datos de Supabase a formato Sale
const convertSupabaseSale = (supabaseSale: any): Sale => ({
  id: supabaseSale.id,
  sale_number: supabaseSale.sale_number,
  customer_name: supabaseSale.customer_name,
  customer_id: supabaseSale.customer_id,
  order_id: supabaseSale.order_id,
  order_number: supabaseSale.order_number, // Número de la orden de pedido
  order_date: supabaseSale.order_date, // Fecha de generación de la orden
  product_id: supabaseSale.product_id,
  quantity: supabaseSale.quantity,
  unit_sale_price: supabaseSale.unit_sale_price,
  unit_cost_price: supabaseSale.unit_cost_price,
  subtotal: supabaseSale.subtotal,
  total_cost: supabaseSale.total_cost,
  net_profit: supabaseSale.net_profit,
  profit_margin: supabaseSale.profit_margin,
  igv_percentage: supabaseSale.igv_percentage,
  igv_amount: supabaseSale.igv_amount,
  total_with_igv: supabaseSale.total_with_igv,
  currency: supabaseSale.currency,
  payment_method: supabaseSale.payment_method,
  payment_terms: supabaseSale.payment_terms,
  payment_type: supabaseSale.payment_type,
  credit_days: supabaseSale.credit_days,
  credit_due_date: supabaseSale.credit_due_date,
  delivery_date: supabaseSale.delivery_date,
  delivery_days: supabaseSale.delivery_days || 5,
  notes: supabaseSale.notes,
  sale_document: supabaseSale.sale_document,
  sale_document_url: supabaseSale.sale_document_url,
  status: supabaseSale.status,
  sale_date: supabaseSale.sale_date,
  created_at: supabaseSale.created_at,
  updated_at: supabaseSale.updated_at
});

// Convertir datos de Supabase a formato SaleWithProduct
const convertSupabaseSaleWithProduct = (supabaseSale: any, product: Product): SaleWithProduct => ({
  ...convertSupabaseSale(supabaseSale),
  product
});

// Convertir datos de Supabase a formato SaleWithDetails
const convertSupabaseSaleWithDetails = (
  supabaseSale: any, 
  product: Product, 
  order?: OrderWithDetails
): SaleWithDetails => ({
  ...convertSupabaseSale(supabaseSale),
  product,
  order
});

// Generar número único de venta
const generateSaleNumber = (): string => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
  return `VT-${year}${month}${day}-${random}`;
};

// Función para obtener el precio de costo promedio de un producto
const getProductCostPrice = async (productId: number): Promise<number> => {
  try {
    console.log('🔍 Buscando compras para producto:', productId);
    
    // Obtener todas las compras del producto para calcular el costo promedio
    const { data: purchases, error: purchaseError } = await supabase
      .from('purchases')
      .select('final_unit_cost, quantity')
      .eq('product_id', productId)
      .order('purchase_date', { ascending: false });

    console.log('📦 Resultado de búsqueda de compras:', { purchases, purchaseError });

    if (!purchaseError && purchases && purchases.length > 0) {
      console.log('📊 Compras encontradas:', purchases.length, 'compras');
      
      // Calcular el costo promedio ponderado por cantidad
      let totalCost = 0;
      let totalQuantity = 0;
      
      purchases.forEach(purchase => {
        totalCost += purchase.final_unit_cost * purchase.quantity;
        totalQuantity += purchase.quantity;
        console.log(`💰 Compra: ${purchase.quantity} unidades a S/ ${purchase.final_unit_cost} cada una`);
      });
      
      console.log('📈 Totales:', { totalCost, totalQuantity });
      
      if (totalQuantity > 0) {
        const averageCost = totalCost / totalQuantity;
        console.log(`✅ Costo promedio calculado para producto ${productId}: S/ ${averageCost.toFixed(2)} (${totalQuantity} unidades, ${purchases.length} compras)`);
        return averageCost;
      }
    }

    console.log('⚠️ No hay compras o hubo error, intentando con precio del producto...');
    
    // Si no hay compras, intentar obtener desde el producto (si tiene un campo de costo)
    const { data: product, error: productError } = await supabase
      .from('products')
      .select('price')
      .eq('id', productId)
      .single();

    console.log('📦 Resultado de búsqueda de producto:', { product, productError });

    if (!productError && product) {
      // Usar el 70% del precio de venta como costo estimado si no hay compras registradas
      const estimatedCost = product.price * 0.7;
      console.log(`✅ Costo estimado para producto ${productId}: S/ ${estimatedCost.toFixed(2)} (70% del precio de venta S/ ${product.price})`);
      return estimatedCost;
    }

    // Si no hay información, retornar 0
    console.log(`⚠️ No se encontró información de costo para producto ${productId}, usando 0`);
    return 0;
  } catch (error) {
    console.error('❌ Error en getProductCostPrice:', error);
    return 0;
  }
};

// Servicio de ventas con Supabase
export const saleService = {
  // Obtener todas las ventas
  getSales: async (): Promise<SaleWithDetails[]> => {
    try {
      const { data, error } = await supabase
        .from('sales')
        .select(`
          *,
          products:product_id (*),
          orders:order_id (*)
        `)
        .order('sale_date', { ascending: false });

      if (error) {
        console.error('Error al obtener ventas:', error);
        return [];
      }

      return data.map((item: any) => {
        const product = item.products;
        const order = item.orders;
        return convertSupabaseSaleWithDetails(item, product, order);
      });
    } catch (error) {
      console.error('Error inesperado al obtener ventas:', error);
      return [];
    }
  },

  // Obtener una venta por ID
  getSaleById: async (id: number): Promise<SaleWithDetails | null> => {
    try {
      const { data, error } = await supabase
        .from('sales')
        .select(`
          *,
          products:product_id (*),
          orders:order_id (*)
        `)
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error al obtener venta por ID:', error);
        return null;
      }

      const product = data.products;
      const order = data.orders;
      return convertSupabaseSaleWithDetails(data, product, order);
    } catch (error) {
      console.error('Error inesperado al obtener venta por ID:', error);
      return null;
    }
  },

  // Obtener datos de una orden para crear una venta
  getSaleDataFromOrder: async (orderId: number): Promise<SaleFromOrderData | null> => {
    try {
      const { data: order, error } = await supabase
        .from('orders')
        .select(`
          *,
          products:product_id (*)
        `)
        .eq('id', orderId)
        .single();

      if (error) {
        console.error('Error al obtener orden:', error);
        return null;
      }

      return {
        order_id: order.id,
        order_number: order.order_number,
        order_date: order.order_date,
        delivery_date: new Date().toISOString().split('T')[0],
        customer_name: order.customer_name,
        customer_id: order.customer_id,
        product_id: order.product_id,
        quantity: order.quantity,
        unit_sale_price: order.unit_price,
        currency: order.currency,
        payment_terms: order.payment_terms,
        delivery_time: order.delivery_time,
        delivery_days: order.delivery_days
      };
    } catch (error) {
      console.error('Error inesperado al obtener datos de orden:', error);
      return null;
    }
  },

  // Crear una nueva venta (método simplificado para generar desde orden)
  createSale: async (saleData: any): Promise<Sale | null> => {
    try {
      console.log('🔄 Iniciando createSale con datos:', JSON.stringify(saleData, null, 2));
      
      // Validar que los datos requeridos estén presentes
      if (!saleData.product_id || !saleData.quantity || !saleData.unit_price) {
        console.error('❌ Datos requeridos faltantes para crear venta:', {
          product_id: saleData.product_id,
          quantity: saleData.quantity,
          unit_price: saleData.unit_price
        });
        return null;
      }
      
      // Obtener el precio de costo del producto
      console.log('🔄 Obteniendo precio de costo para producto:', saleData.product_id);
      const unitCostPrice = await getProductCostPrice(saleData.product_id);
      console.log('💰 Costo unitario obtenido:', unitCostPrice);
      
      // Calcular montos
      const subtotal = saleData.subtotal;
      const totalCost = unitCostPrice * saleData.quantity;
      const netProfit = subtotal - totalCost;
      const profitMargin = subtotal > 0 ? (netProfit / subtotal) * 100 : 0;
      const igvAmount = saleData.igv_amount || (subtotal * 0.18);
      const totalWithIgv = saleData.total_with_igv || (subtotal + igvAmount);

      console.log('📊 Cálculos financieros:', {
        subtotal,
        totalCost,
        netProfit,
        profitMargin,
        igvAmount,
        totalWithIgv
      });

      const saleDataToInsert = {
        sale_number: generateSaleNumber(),
        customer_name: saleData.customer_name,
        customer_id: saleData.customer_id,
        order_id: saleData.order_id,
        order_number: saleData.order_number || null, // Número de la orden de pedido
        order_date: saleData.order_date || null, // Fecha de generación de la orden
        product_id: saleData.product_id,
        quantity: saleData.quantity,
        unit_sale_price: saleData.unit_price,
        unit_cost_price: unitCostPrice,
        subtotal: subtotal,
        total_cost: totalCost,
        net_profit: netProfit,
        profit_margin: profitMargin,
        igv_percentage: saleData.igv_percentage || 18,
        igv_amount: igvAmount,
        total_with_igv: totalWithIgv,
        currency: saleData.currency || 'USD',
        payment_method: saleData.payment_method || 'transferencia',
        payment_terms: saleData.payment_terms || 'Contado',
        payment_type: saleData.payment_type || 'contado', // Tipo de pago desde la orden
        credit_days: saleData.credit_days || 0, // Días de crédito desde la orden
        delivery_date: saleData.delivery_date,
        notes: saleData.notes,
        status: 'Generada',
        sale_date: saleData.sale_date
      };

      console.log('💾 Datos a insertar en Supabase:', JSON.stringify(saleDataToInsert, null, 2));

      const { data, error } = await supabase
        .from('sales')
        .insert([saleDataToInsert])
        .select()
        .single();

      if (error) {
        console.error('❌ Error al crear venta:', error);
        console.error('Detalles del error:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        console.error('Error completo:', JSON.stringify(error, null, 2));
        console.error('Datos que se intentaron insertar:', JSON.stringify(saleDataToInsert, null, 2));
        return null;
      }

      console.log('✅ Venta insertada exitosamente:', data);

      // Actualizar el stock del producto solo si no hay referencia de orden
      // Si hay orden de pedido, el stock ya fue restado cuando se creó la orden
      if (!saleData.order_id) {
        await updateProductStock(saleData.product_id, -saleData.quantity);
        console.log(`📦 Stock actualizado: -${saleData.quantity} unidades del producto ${saleData.product_id} (venta sin referencia)`);
      } else {
        console.log(`📦 Stock no modificado: venta con referencia a orden ${saleData.order_id}`);
      }

      return convertSupabaseSale(data);
    } catch (error) {
      console.error('❌ Error inesperado al crear venta:', error);
      if (error instanceof Error) {
        console.error('Stack trace:', error.stack);
      }
      return null;
    }
  },

  // Agregar una nueva venta
  addSale: async (saleData: SaleFormData): Promise<Sale | null> => {
    try {
      console.log('🔄 Iniciando creación de venta con datos:', saleData);
      
      // Verificar autenticación
      const { data: { session }, error: authError } = await supabase.auth.getSession();
      if (authError || !session) {
        console.error('❌ Error de autenticación:', authError);
        console.error('❌ No hay sesión activa');
        return null;
      }
      console.log('✅ Usuario autenticado:', session.user?.email);
      
      // Validar que hay items
      if (!saleData.items || saleData.items.length === 0) {
        console.error('❌ Error: No hay items en la venta');
        return null;
      }
      
      // Tomar el primer producto como producto principal
      const firstItem = saleData.items[0];
      console.log('📦 Primer item:', firstItem);
      
      // Obtener el precio de costo del producto
      const unitCostPrice = await getProductCostPrice(firstItem.product_id);
      console.log('💰 Costo unitario obtenido:', unitCostPrice);
      
      // Calcular montos basados en los items
      const subtotal = saleData.items.reduce((sum, item) => sum + item.subtotal, 0);
      const totalCost = saleData.items.reduce((sum, item) => sum + item.total_cost, 0);
      const netProfit = subtotal - totalCost;
      const profitMargin = subtotal > 0 ? (netProfit / subtotal) * 100 : 0;
      const igvAmount = subtotal * 0.18; // 18% de IGV
      const totalWithIgv = subtotal + igvAmount;
      
      console.log('📊 Cálculos financieros:', {
        subtotal,
        totalCost,
        netProfit,
        profitMargin,
        igvAmount,
        totalWithIgv
      });

      // Subir documento de venta si existe y es un archivo File
      let saleDocumentUrl = '';
      if (saleData.sale_document && saleData.sale_document instanceof File) {
        try {
          const uploadResult = await uploadImage(saleData.sale_document, {
            folder: 'atomic-corporation/sales',
            resource_type: 'auto'
          });

          saleDocumentUrl = uploadResult.secure_url;
          console.log('✅ Documento de venta subido exitosamente a Cloudinary:', uploadResult.secure_url);
        } catch (uploadError) {
          console.error('Error al subir documento de venta:', uploadError);
          // Continuar sin el documento si hay error en la subida
        }
      }

      const saleDataToInsert = {
        sale_number: saleData.sale_number || generateSaleNumber(),
        customer_name: saleData.customer_name,
        customer_id: saleData.customer_id,
        order_id: saleData.order_id,
        order_number: saleData.order_number || null, // Agregar número de orden
        order_date: saleData.order_date || null, // Agregar fecha de orden
        product_id: firstItem?.product_id || 0,
        quantity: firstItem?.quantity || 0,
        unit_sale_price: parseFloat((firstItem?.unit_sale_price || 0).toString()),
        unit_cost_price: parseFloat(unitCostPrice.toString()),
        subtotal: parseFloat(subtotal.toString()),
        total_cost: parseFloat(totalCost.toString()),
        net_profit: parseFloat(netProfit.toString()),
        profit_margin: parseFloat(profitMargin.toString()),
        igv_percentage: parseFloat('18'),
        igv_amount: parseFloat(igvAmount.toString()),
        total_with_igv: parseFloat(totalWithIgv.toString()),
        currency: saleData.currency || 'USD',
        payment_method: saleData.payment_method,
        payment_terms: saleData.payment_terms || 'Crédito a 30 días',
        payment_type: saleData.payment_type || 'contado',
        credit_days: saleData.credit_days || 0,
        credit_due_date: saleData.credit_due_date,
        calculate_detraccion: saleData.detraccion_enabled || false,
        detraccion_percentage: parseInt((saleData.detraccion_percentage || 12).toString()),
        detraccion_amount: parseFloat((saleData.detraccion_amount || 0).toString()),
        delivery_date: saleData.delivery_date,
        delivery_days: saleData.delivery_days || 5,
        notes: saleData.notes,
        sale_document: saleData.sale_document?.name,
        sale_document_url: saleDocumentUrl,
        status: 'pending',
        sale_date: saleData.sale_date
      };

      // No eliminar payment_type ya que este campo sí existe en la tabla
      
      console.log('💾 Datos a insertar en Supabase:', saleDataToInsert);

      const { data, error } = await supabase
        .from('sales')
        .insert([saleDataToInsert])
        .select()
        .single();

      if (error) {
        console.error('❌ Error al agregar venta en Supabase:', error);
        console.error('Detalles del error:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        console.error('Error completo:', JSON.stringify(error, null, 2));
        console.error('Datos que se intentaron insertar:', JSON.stringify(saleDataToInsert, null, 2));
        return null;
      }

      console.log('✅ Venta insertada exitosamente:', data);

      // Actualizar el stock de todos los productos solo si no hay referencia de orden
      if (!saleData.order_id) {
        for (const item of saleData.items) {
          await updateProductStock(item.product_id, -item.quantity);
          console.log(`📦 Stock actualizado: -${item.quantity} unidades del producto ${item.product_id} (venta sin referencia)`);
        }
      } else {
        console.log(`📦 Stock no modificado: venta con referencia a orden ${saleData.order_id}`);
      }

      return convertSupabaseSale(data);
    } catch (error) {
      console.error('❌ Error inesperado al agregar venta:', error);
      if (error instanceof Error) {
        console.error('Stack trace:', error.stack);
      }
      return null;
    }
  },

  // Actualizar una venta existente
  updateSale: async (id: number, updatedSale: Partial<SaleFormData>): Promise<Sale | null> => {
    try {
      const updateData: any = {};
      
      if (updatedSale.customer_name !== undefined) updateData.customer_name = updatedSale.customer_name;
      if (updatedSale.customer_id !== undefined) updateData.customer_id = updatedSale.customer_id;
      if (updatedSale.order_id !== undefined) updateData.order_id = updatedSale.order_id;
      if (updatedSale.order_number !== undefined) updateData.order_number = updatedSale.order_number;
      if (updatedSale.order_date !== undefined) updateData.order_date = updatedSale.order_date;
      if (updatedSale.currency !== undefined) updateData.currency = updatedSale.currency;
      if (updatedSale.payment_method !== undefined) updateData.payment_method = updatedSale.payment_method;
      if (updatedSale.payment_terms !== undefined) updateData.payment_terms = updatedSale.payment_terms;
      if (updatedSale.payment_type !== undefined) updateData.payment_type = updatedSale.payment_type;
      if (updatedSale.credit_days !== undefined) updateData.credit_days = updatedSale.credit_days;
      if (updatedSale.credit_due_date !== undefined) updateData.credit_due_date = updatedSale.credit_due_date;
      if (updatedSale.detraccion_enabled !== undefined) updateData.detraccion_enabled = updatedSale.detraccion_enabled;
      if (updatedSale.detraccion_percentage !== undefined) updateData.detraccion_percentage = updatedSale.detraccion_percentage;
      if (updatedSale.detraccion_amount !== undefined) updateData.detraccion_amount = updatedSale.detraccion_amount;
      if (updatedSale.delivery_date !== undefined) updateData.delivery_date = updatedSale.delivery_date;
      if (updatedSale.delivery_days !== undefined) updateData.delivery_days = updatedSale.delivery_days;
      if (updatedSale.notes !== undefined) updateData.notes = updatedSale.notes;
      if (updatedSale.sale_date !== undefined) updateData.sale_date = updatedSale.sale_date;
      if (updatedSale.sale_number !== undefined) updateData.sale_number = updatedSale.sale_number;

      // Recalcular montos si se modificaron los items
      if (updatedSale.items !== undefined) {
        // Obtener la venta actual para los valores que no se actualizan
        const currentSale = await saleService.getSaleById(id);
        if (currentSale) {
          const items = updatedSale.items;
          
          const subtotal = items.reduce((sum, item) => sum + item.subtotal, 0);
          const totalCost = items.reduce((sum, item) => sum + item.total_cost, 0);
          const netProfit = subtotal - totalCost;
          const profitMargin = subtotal > 0 ? (netProfit / subtotal) * 100 : 0;
          const igvAmount = subtotal * 0.18;
          const totalWithIgv = subtotal + igvAmount;
          
          updateData.subtotal = subtotal;
          updateData.total_cost = totalCost;
          updateData.net_profit = netProfit;
          updateData.profit_margin = profitMargin;
          updateData.igv_amount = igvAmount;
          updateData.total_with_igv = totalWithIgv;
          
          // Actualizar el primer producto para compatibilidad con la estructura actual
          if (items.length > 0) {
            const firstItem = items[0];
            updateData.product_id = firstItem.product_id;
            updateData.quantity = firstItem.quantity;
            updateData.unit_sale_price = firstItem.unit_sale_price;
            updateData.unit_cost_price = firstItem.unit_cost_price;
          }
        }
      }

      // Subir nuevo documento de venta solo si se proporciona un nuevo archivo File
      if (updatedSale.sale_document && updatedSale.sale_document instanceof File) {
        try {
          console.log('🔄 Subiendo nuevo documento para venta actualizada...');
          const uploadResult = await uploadImage(updatedSale.sale_document, {
            folder: 'atomic-corporation/sales',
            resource_type: 'auto'
          });
          updateData.sale_document = updatedSale.sale_document.name;
          updateData.sale_document_url = uploadResult.secure_url;
          console.log('✅ Nuevo documento de venta subido exitosamente:', uploadResult.secure_url);
        } catch (uploadError) {
          console.error('Error al subir documento de venta:', uploadError);
        }
      }

      const { data, error } = await supabase
        .from('sales')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error al actualizar venta:', error);
        return null;
      }

      return convertSupabaseSale(data);
    } catch (error) {
      console.error('Error inesperado al actualizar venta:', error);
      return null;
    }
  },

  // Actualizar estado de una venta
  updateSaleStatus: async (id: number, status: Sale['status']): Promise<Sale | null> => {
    try {
      // Obtener la venta actual para verificar si es de crédito
      const { data: currentSale, error: fetchError } = await supabase
        .from('sales')
        .select('payment_type, credit_days, credit_due_date')
        .eq('id', id)
        .single();

      if (fetchError) {
        console.error('Error al obtener venta actual:', fetchError);
        return null;
      }

      const updateData: any = {
        status,
        updated_at: new Date().toISOString()
      };

      // Si la venta está pasando a estado "Facturada" y es de crédito, calcular fecha de vencimiento
      if (status === 'Facturada' && currentSale.payment_type === 'credito' && currentSale.credit_days > 0) {
        const today = new Date();
        const dueDate = new Date(today);
        dueDate.setDate(today.getDate() + currentSale.credit_days);
        
        updateData.credit_due_date = dueDate.toISOString().split('T')[0];
        console.log(`📅 Fecha de vencimiento calculada para venta ${id}: ${updateData.credit_due_date} (${currentSale.credit_days} días)`);
      }

      const { data, error } = await supabase
        .from('sales')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error al actualizar estado de venta:', error);
        return null;
      }

      return convertSupabaseSale(data);
    } catch (error) {
      console.error('Error inesperado al actualizar estado de venta:', error);
      return null;
    }
  },

  // Eliminar una venta
  deleteSale: async (id: number): Promise<boolean> => {
    try {
      // Obtener la venta antes de eliminarla para actualizar el stock
      const sale = await saleService.getSaleById(id);
      if (!sale) {
        return false;
      }

      const { error } = await supabase
        .from('sales')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error al eliminar venta:', error);
        return false;
      }

      // Devolver el stock al producto solo si la venta no tenía referencia de orden
      if (!sale.order_id) {
        await updateProductStock(sale.product.id, sale.quantity);
        console.log(`📦 Stock devuelto: +${sale.quantity} unidades del producto ${sale.product.id} (venta sin referencia)`);
      } else {
        console.log(`📦 Stock no modificado: venta con referencia a orden ${sale.order_id}`);
      }

      return true;
    } catch (error) {
      console.error('Error inesperado al eliminar venta:', error);
      return false;
    }
  },

  // Obtener ventas por estado
  getSalesByStatus: async (status: Sale['status']): Promise<SaleWithDetails[]> => {
    try {
      const { data, error } = await supabase
        .from('sales')
        .select(`
          *,
          products:product_id (*),
          orders:order_id (*)
        `)
        .eq('status', status)
        .order('sale_date', { ascending: false });

      if (error) {
        console.error('Error al obtener ventas por estado:', error);
        return [];
      }

      return data.map((item: any) => {
        const product = item.products;
        const order = item.orders;
        return convertSupabaseSaleWithDetails(item, product, order);
      });
    } catch (error) {
      console.error('Error inesperado al obtener ventas por estado:', error);
      return [];
    }
  },

  // Obtener estadísticas de ventas
  getSaleStats: async (): Promise<SaleStats> => {
    try {
      const { data, error } = await supabase
        .from('sales')
        .select('status, subtotal, net_profit, profit_margin');

      if (error) {
        console.error('Error al obtener estadísticas de ventas:', error);
        return {
          total: 0,
          generated: 0,
          invoiced: 0,
          overdue: 0,
          completed: 0,
          totalRevenue: 0,
          totalProfit: 0,
          averageProfitMargin: 0
        };
      }

      const stats = {
        total: data?.length || 0,
        generated: data?.filter(s => s.status === 'Generada').length || 0,
        invoiced: data?.filter(s => s.status === 'Facturada').length || 0,
        overdue: data?.filter(s => s.status === 'Vencida').length || 0,
        completed: data?.filter(s => s.status === 'Completada').length || 0,
        totalRevenue: data?.reduce((sum, s) => sum + (s.subtotal || 0), 0) || 0,
        totalProfit: data?.reduce((sum, s) => sum + (s.net_profit || 0), 0) || 0,
        averageProfitMargin: 0
      };

      // Calcular margen de ganancia promedio solo para ventas completadas
      const completedSales = data?.filter(s => s.status === 'Completada') || [];
      if (completedSales.length > 0) {
        const totalMargin = completedSales.reduce((sum, s) => sum + (s.profit_margin || 0), 0);
        stats.averageProfitMargin = totalMargin / completedSales.length;
      }

      return stats;
    } catch (error) {
      console.error('Error inesperado al obtener estadísticas de ventas:', error);
      return {
        total: 0,
        generated: 0,
        invoiced: 0,
        overdue: 0,
        completed: 0,
        totalRevenue: 0,
        totalProfit: 0,
        averageProfitMargin: 0
      };
    }
  },

  // Verificar y actualizar ventas vencidas
  checkAndUpdateOverdueSales: async (): Promise<number> => {
    try {
      console.log('🔍 Verificando ventas vencidas...');
      
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Inicio del día para comparación precisa
      
      // Obtener todas las ventas que están en estado 'Generada' o 'Facturada' y son de crédito
      const { data: sales, error } = await supabase
        .from('sales')
        .select(`
          id,
          sale_number,
          customer_name,
          payment_terms,
          payment_type,
          credit_days,
          credit_due_date,
          status,
          sale_date
        `)
        .in('status', ['Generada', 'Facturada'])
        .not('credit_due_date', 'is', null)
        .gt('credit_days', 0);

      if (error) {
        console.error('Error al obtener ventas para verificar vencimiento:', error);
        return 0;
      }

      if (!sales || sales.length === 0) {
        console.log('📋 No hay ventas de crédito para verificar');
        return 0;
      }

      let updatedCount = 0;

      for (const sale of sales) {
        // Verificar si la venta tiene fecha de vencimiento y es de crédito
        if (sale.credit_due_date) {
          const dueDate = new Date(sale.credit_due_date);
          dueDate.setHours(0, 0, 0, 0); // Inicio del día para comparación precisa
          
          // Si la fecha de vencimiento es anterior a hoy y la venta no está vencida
          if (dueDate < today && sale.status !== 'Vencida') {
            console.log(`⏰ Venta vencida detectada: ${sale.sale_number} (Vencía: ${sale.credit_due_date})`);
            
            // Actualizar el estado a 'Vencida'
            const { error: updateError } = await supabase
              .from('sales')
              .update({
                status: 'Vencida',
                updated_at: new Date().toISOString()
              })
              .eq('id', sale.id);

            if (!updateError) {
              updatedCount++;
              console.log(`✅ Venta ${sale.sale_number} marcada como Vencida`);
            } else {
              console.error(`❌ Error al actualizar venta ${sale.sale_number}:`, updateError);
            }
          }
        }
      }

      if (updatedCount > 0) {
        console.log(`📊 Se actualizaron ${updatedCount} ventas como vencidas`);
      } else {
        console.log('📊 No hay ventas vencidas para actualizar');
      }

      return updatedCount;
    } catch (error) {
      console.error('Error inesperado al verificar ventas vencidas:', error);
      return 0;
    }
  }
};

// Función auxiliar para actualizar el stock de un producto
const updateProductStock = async (productId: number, quantityToAdd: number): Promise<void> => {
  try {
    // Obtener el stock actual del producto
    const { data: product, error: productError } = await supabase
      .from('products')
      .select('stock')
      .eq('id', productId)
      .single();

    if (productError) {
      console.error('Error al obtener stock del producto:', productError);
      return;
    }

    // Calcular nuevo stock
    const currentStock = product.stock || 0;
    const newStock = Math.max(0, currentStock + quantityToAdd);

    // Actualizar stock y estado inStock
    const { error: updateError } = await supabase
      .from('products')
      .update({
        stock: newStock,
        in_stock: newStock > 0
      })
      .eq('id', productId);

    if (updateError) {
      console.error('Error al actualizar stock del producto:', updateError);
    }
  } catch (error) {
    console.error('Error inesperado al actualizar stock:', error);
  }
};